export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

export const ATTACH_ACCEPT =
  "image/png,image/jpeg,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.png,.jpg,.jpeg,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx";

const MIME_BY_EXT: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".gif": "image/gif",
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xls": "application/vnd.ms-excel",
  ".xlsx":
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

const ALLOWED = new Set(Object.values(MIME_BY_EXT));

export type PendingAttachment = {
  id: string;
  name: string;
  mimeType: string;
  dataUrl: string;
  base64: string;
};

export type AttachmentView = {
  url: string;
  name?: string;
  mimeType?: string;
};

function extFromName(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
}

export function resolveBrowserMime(file: File): string {
  const type = (file.type || "").split(";")[0]?.trim().toLowerCase();
  if (type && ALLOWED.has(type)) return type;
  if (type.startsWith("image/")) return type;
  return MIME_BY_EXT[extFromName(file.name)] || type;
}

export function isAllowedBrowserFile(file: File): boolean {
  const mime = resolveBrowserMime(file);
  return ALLOWED.has(mime) || mime.startsWith("image/");
}

export function isImageMime(mime: string | undefined | null): boolean {
  return String(mime || "").toLowerCase().startsWith("image/");
}

export function isImageView(item: AttachmentView): boolean {
  if (item.mimeType) return isImageMime(item.mimeType);
  if (item.url.startsWith("data:image/")) return true;
  if (item.url.startsWith("data:")) return false;
  return /\.(png|jpe?g|webp|gif)(\?|#|$)/i.test(item.url);
}

export function fileKindLabel(mime: string | undefined, name?: string): string {
  const resolved = String(mime || "").toLowerCase();
  if (resolved.startsWith("image/")) return "Image";
  if (resolved === "application/pdf") return "PDF";
  if (resolved.includes("word") || /\.docx?$/i.test(name || "")) return "Word";
  if (resolved.includes("excel") || resolved.includes("spreadsheet") || /\.xlsx?$/i.test(name || "")) {
    return "Excel";
  }
  const ext = extFromName(name || "").replace(".", "").toUpperCase();
  return ext || "File";
}

export function toAttachmentView(
  item: string | AttachmentView
): AttachmentView {
  return typeof item === "string" ? { url: item } : item;
}
